/**
 * zod가 필요 없는 상수와 타입만 모아 둔 하위 경로 `@apm/contracts/consts` (14회차).
 *
 * 에이전트는 대상 앱 프로세스 안에서 돈다. 거기 zod를 끌고 들어가면 스키마 객체를 만드느라
 * 무부하 RSS가 11.5MB 늘고 모듈이 100개 넘게 붙는데, 정작 에이전트가 **런타임에** 쓰는 것은
 * 아래 상수 넷뿐이다. 나머지 계약(zod 스키마)은 `import type`으로만 쓴다.
 *
 * 여기에는 `zod` import를 넣지 않는다. 최상위 `@apm/contracts`가 이 파일을 다시 내보내므로
 * 서버·콘솔은 지금까지처럼 `@apm/contracts` 하나만 보면 된다.
 * (`@apm/core/gzip`이 node 전용 코드를 하위 경로로 뺀 것과 같은 이유·같은 방식이다.)
 */

/**
 * 트랜잭션 타입. 모든 통계의 1차 분할 기준이라 계측 시작 시점부터 분류한다.
 * - http:   일반 REST 요청
 * - sse:    장시간 유지되는 SSE 연결 (`…/stream`으로 끝나는 경로 3개)
 * - mcp:    `/mcp` 요청 (응답은 순수 JSON)
 * - worker: 내부 배치 워커 (연차발생·연차촉진·알림 아웃박스)
 * - pdf:    PDF 렌더 (`GET /api/v2/knowledge/articles/:id/pdf`, 최대 30초)
 * - slow:   오래 걸리는 것이 정상인 HTTP 요청 (LLM 호출, 대용량 업로드 등).
 *           26회차에 계약에만 늘렸고 **28회차에 에이전트가 실제로 붙이기 시작했다**
 *           (2026-09-09 결정 D-5). 분류는 `txType.slow` 경로 목록으로 한다 — 소요 시간
 *           임계로는 가를 수 없다. 분류가 span **시작** 시점이라 그때는 걸린 시간을 모른다.
 *           경로 항목에 `POST /upload/:id`처럼 메서드를 앞에 붙일 수 있다. 같은 URL에
 *           빠른 GET(페이지 렌더)과 느린 POST(Server Action)가 함께 오는 앱 때문이다.
 *
 * 이 목록에 값을 더하면 저장된 옛 행은 그대로 있고 새 행만 새 타입을 쓴다. **값을 지우거나
 * 뜻을 바꾸지 않는다** — 옛 파일의 그 값이 계약 검증에 걸려 조회에서 통째로 사라진다.
 */
export const TX_TYPES = ['http', 'sse', 'mcp', 'worker', 'pdf', 'slow'] as const;
export type TxType = (typeof TX_TYPES)[number];

/**
 * 저장·조회가 **받는** 타입. **28회차에 `TX_TYPES`와 하나로 합쳤다.**
 *
 * 26회차에 `slow`를 계약에만 늘리면서 둘을 갈라 두었다. 에이전트의 activeTx 버킷이 타입마다
 * 배열 하나라 목록을 늘리면 그 코드가 함께 바뀌어야 해서, 에이전트가 `slow`를 붙이기 전까지
 * 저장·조회만 먼저 받게 한 것이다. 28회차에 에이전트가 붙이기 시작해 나눌 이유가 없어졌다.
 *
 * 이름은 남긴다. 서버 코드가 이 이름을 보고 있고, "저장이 받는 것"과 "에이전트가 만드는 것"은
 * 뜻으로는 여전히 다른 축이라 언젠가 다시 갈릴 수 있다.
 */
export const STORED_TX_TYPES = TX_TYPES;
export type StoredTxType = TxType;

/**
 * activeTx 버킷에서 개별 항목을 싣는 타입 (sse 제외).
 * sse는 연결이 수십 분 유지돼 상위 N건 정렬을 독차지하므로 건수·최장 연결만 보낸다.
 *
 * `slow`는 28회차에 들어왔다. 오래 걸리는 것이 정상인 요청이라 목록에서 특히 잘 보여야 한다.
 * 옛 에이전트는 이 버킷을 안 보내므로 스냅샷 계약이 기본값 `[]`을 준다.
 */
export const LISTED_TX_TYPES = [
  'http',
  'mcp',
  'worker',
  'pdf',
  'slow',
] as const satisfies readonly TxType[];

/**
 * span 속성 키. 에이전트가 붙이고 서버가 읽는 이름이라 **계약**이다 (13회차).
 *
 * 13회차 전에는 `apm.tx_type`·`apm.tenant_id` 문자열이 에이전트(`tx-type.ts`·`tenant.ts`)와
 * 서버(`normalizer.ts`)에 따로 적혀 있었다. semconv 키 목록도 세 파일에 복제돼 있었고,
 * 실제로 `registry.ts`와 `normalizer.ts`의 `ROUTE_KEYS`가 달라질 수 있는 상태였다.
 * 한쪽만 고치면 조용히 분류가 어긋나므로 여기 하나만 둔다.
 */
export const APM_ATTR = {
  /** 트랜잭션 타입(`http`·`sse`·`mcp`·`worker`·`pdf`·`slow`). 분류 processor가 root span에 붙인다 */
  txType: 'apm.tx_type',
  /** 테넌트 id(정수 문자열). 요청 컨텍스트에서 뽑아 root span에 붙인다 */
  tenantId: 'apm.tenant_id',
  /**
   * SQL 바인드 값. JSON 배열 문자열(`["1","홍길동"]`)이다 (2026-09-07 결정 7).
   * pg 계측이 싣는 이름은 버전마다 다르므로(지금은 `db.postgresql.values`)
   * 에이전트가 이 키 하나로 정규화해서 보낸다. 서버는 이 키만 읽는다
   */
  sqlParams: 'apm.sql_params',
  /**
   * SQL 문장이 전송 상한(`exporter.attributeValueLengthLimit`)에 잘렸는지(불리언).
   * 에이전트가 잘린 문장에만 참으로 붙인다(22-C회차, UI 피드백 3). SDK가 `setAttribute` 시점에
   * 자르므로 원문 길이는 pg 계측의 `requestHook`에서만 알 수 있다. 서버는 `SpanRecord.queryTruncated`로
   * 그대로 넘긴다
   */
  sqlTruncated: 'apm.sql_truncated',
  /** 잘리기 전 SQL 문장의 길이(자). `apm.sql_truncated`가 참일 때만 붙는다 */
  sqlOriginalLength: 'apm.sql_original_length',
  /**
   * 바인드 값 목록이 원소 수·총 길이 상한에 잘렸는지(불리언). 잘렸을 때만 참이다 (D-25/Q-27).
   * `apm.sql_params`는 span이 끝난 뒤 `attributes`에 직접 쓰므로 SDK 길이 상한을 타지 않는다.
   * 에이전트가 따로 자른다
   */
  sqlParamsTruncated: 'apm.sql_params_truncated',
  /**
   * 5xx로 끝난 요청의 스택 상위 프레임. **JSON 배열 문자열**이다 (25회차, 조정자 제안 7).
   *
   * `apm.sql_params`와 같은 방식으로 싣는다 — OTLP JSON의 배열 값(`arrayValue`)은 서버
   * 파서가 읽지 않으므로 배열은 언제나 JSON 문자열 하나로 보낸다.
   *
   * 에이전트는 **HTTP 상태 500 이상인 요청에만** 단다. 4xx·정상 요청에는 이 키가 없다.
   * 프레임은 앞 5개, 하나에 200자까지고 `node_modules` 프레임은 뺀다. 스택 원문은
   * 어떤 경우에도 전송하지 않는다
   */
  errorFrames: 'apm.error_frames',
} as const;

/**
 * OTel 시맨틱 규약 키. 계측 버전에 따라 옛 이름이 오기도 해서 **앞에 있는 것부터** 본다.
 * 새 이름(`http.request.method`)이 먼저, 옛 이름(`http.method`)이 뒤다.
 */
export const SEMCONV = {
  /** HTTP 메서드 */
  method: ['http.request.method', 'http.method'],
  /** 라우트 템플릿 → 경로 → 옛 target 순 */
  route: ['http.route', 'url.path', 'http.target'],
  /** 응답 상태 코드 */
  status: ['http.response.status_code', 'http.status_code'],
  /** 클라이언트 span의 대상 URL */
  extUrl: ['url.full', 'http.url'],
  /**
   * 나가는 호출이 부른 상대(호스트). 표준 계측의 CLIENT span 에 늘 실린다.
   * 요청 안이 아니라 홀로 온 나가는 호출의 진입점을 이것으로 적는다 (31회차)
   */
  serverAddress: ['server.address', 'net.peer.name', 'net.peer.ip'],
  /**
   * 나가는 호출이 부른 상대의 포트 (37회차). `server.address`와 **짝으로** 읽는다.
   * 값이 있으면 기본 포트(80·443)라도 생략하지 않고 그대로 붙인다 — 계측이 적어 보낸 것을
   * 우리가 지우면 `api:443`과 `api`가 다른 앱에서 다른 줄이 된다
   */
  serverPort: ['server.port', 'net.peer.port'],
  /** SQL 문장 */
  sqlText: ['db.query.text', 'db.statement'],
  /** DB 구간인지 가리는 키. 질의가 아닌 연결·풀 span 에도 붙는다 */
  db: ['db.system.name', 'db.system', 'db.query.text', 'db.statement'],
  /**
   * 질의 연산 이름(`SELECT`·`INSERT`·프로시저 이름 등). 문장이 잘려 나가도 계측이 이것은 싣는다.
   * **질의 근거**로 쓴다 — `db.system`만 있는 연결·풀 span 을 SQL 로 세지 않기 위해서다
   * (29회차 리뷰 R29-06)
   */
  dbOperation: ['db.operation.name', 'db.operation'],
  /** SQLSTATE를 속성으로 실어 주는 계측을 위한 자리 */
  dbErrorCode: ['db.response.status_code', 'db.error.code', 'error.code'],
  /** 클라이언트 주소. 새 이름이 먼저, 옛 이름이 뒤다 (2026-09-07 결정 3·11) */
  clientAddress: ['client.address', 'net.peer.ip', 'http.client_ip'],
  /** User-Agent 원문 */
  userAgent: ['user_agent.original', 'http.user_agent'],
  /**
   * 요청을 보낸 화면(Referer 헤더). http 계측이 `headersToSpanAttributes`로 싣는 이름이다
   * (22-C회차, UI 피드백 8). 계측은 배열로 싣고 에이전트가 문자열 하나로 편다.
   * URL이라 쿼리의 서명 토큰(`st=`)은 마스킹기가 지운다
   */
  referer: ['http.request.header.referer'],
  /**
   * 응답 본문 크기(바이트). 에이전트가 `content-length` 헤더에서 읽어 단다 (22-C회차, UI 피드백 10).
   *
   * 마지막 후보는 **잡은 응답 헤더**다 (36회차, 31회차 D-5). 표준 계측(Java·.NET)은 기본
   * 설정에서 `http.response.body.size`를 안 만들고, 켤 수 있는 것은 응답 헤더 캡처
   * (`OTEL_INSTRUMENTATION_HTTP_SERVER_CAPTURE_RESPONSE_HEADERS=content-length`)뿐이다.
   * semconv 대로 값이 하나여도 **배열**로 온다.
   *
   * 읽는 쪽은 **후보마다** 따로 본다 - "비어 있지 않은 10진 정수이고 0 이상"일 때만 받고,
   * 아니면 다음 후보로 내려간다. 잘못된 상위 후보가 멀쩡한 아래 후보를 가리면 안 되기 때문이다.
   * 배열은 값이 하나이거나 **같은 값이 되풀이된 것**만 받는다. 서로 다른 길이가 실려 오면
   * 어느 쪽이 맞는지 알 수 없어 버린다.
   */
  responseSize: [
    'http.response.body.size',
    'http.response_content_length',
    'http.response.header.content-length',
  ],
} as const satisfies Record<string, readonly string[]>;
